<script lang="ts">
  import type { HTMLInputAttributes } from "svelte/elements";
  import Lock from "~icons/ph/lock";
  import Plus from "~icons/ph/plus-circle-bold";
  import Trash from "~icons/ph/trash";
  import TextAction from "../../components/uiform/TextAction.svelte";
  import { getStep } from "../../lib/workflow/workflowLib.svelte";

  let {
    existing = $bindable(),
    items,
    set,
    label,
    type = "text",
    autocomplete,
    placeholder,
    open = false,
    required: atLeastOneRequired,
    error,
  }: {
    existing: string;
    items: string[];
    set: (item: string[]) => void;
    label: string;
    type?: HTMLInputAttributes["type"];
    autocomplete?: HTMLInputAttributes["autocomplete"];
    placeholder?: string;
    open?: boolean;
    required?: HTMLInputAttributes["required"];
    error: string | undefined;
  } = $props();

  const step = getStep();

  let value = $state<string>("");
  let required = $derived(atLeastOneRequired && !items.length);
  let textAction = $state<TextAction>();
  // svelte-ignore state_referenced_locally
  let showAdd = $state<boolean>(open);

  function add(textValue:string) {
    const trimmed = textValue.trim();
    set([...items, trimmed]);
    value = "";
  }

  // Mirrors server-side EmailAddressFormat and C4C signup; custom regex from https://stackoverflow.com/a/75050279 approximating RFC 5322
  const emailPattern = /^[a-z0-9!#$%&'*+\/=?^_‘{|}~-]+(?:\.[a-z0-9!#$%&'*+\/=?^_‘{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;

  const validateEmail = (value: string) => emailPattern.test(value) ? "" : "Please enter a valid email address.";

  $effect(() => { if (showAdd && step.active) textAction?.focus() });
</script>

<div class="flex flex-col gap-2 mb-4">
  <output class="ui-input py-4 bg-blue-50 bg-opacity-50 border border-blue-200 flex items-center justify-between min-h-14">
    {existing}
    <div class="text-gray-500 text-xs flex items-center gap-1"><Lock class="h-4 w-4"/>On file</div>
  </output>

  {#each items as item}
    <div class="flex justify-between items-start gap-2 border rounded-lg p-4">
      <span class="break-words min-w-0">{item}</span>
      <button onclick={() => set(items.filter((e) => e !== item))} type="button" class="text-blue-600 -mt-0.2">
        <Trash class="h-6 w-6 shrink-0" />
      </button>
    </div>
  {/each}
</div>

{#if showAdd}
  <TextAction
    bind:this={textAction}
    bind:value={value}
    action={add}
    clean={required ? "Add to continue" : true}
    buttonClass="btn-primary"
    validator={type === "email" ? validateEmail : undefined}
    {type} {autocomplete} {placeholder} {required}
  />
{:else}
  <label class="text-blue-600 underline font-semibold flex gap-2 items-center">
    <input
      type="checkbox"
      class="sr-only"
      onclick={() => showAdd = true}
      oninvalid={(e) => e.currentTarget.setCustomValidity(`Please add at least one ${label}`)}
      {required}
    >
    <Plus class="w-4 h-4" />Add {label}
  </label>
{/if}
{#if error}<div class="ui-error mt-1.5">{error}</div>{/if}
